<template>
  <div class="content-wrapper">
    <div class="content-body">
      <b-card>
        <!-- En-tête : titre + recherche + création -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="LayersIcon"
              size="18"
              class="mr-50 text-primary"
            />
            <h4 class="mb-0">
              Projets d'amélioration continue
            </h4>
          </div>
          <b-button
            v-if="$can('CHANTIER:create')"
            variant="primary"
            @click="openCreate"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Nouveau projet
          </b-button>
        </div>

        <b-input-group class="mb-1">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" />
          </b-input-group-prepend>
          <b-form-input
            v-model="search"
            placeholder="Rechercher un projet…"
            debounce="300"
          />
        </b-input-group>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <b-spinner variant="primary" />
        </div>

        <b-alert
          v-else-if="error"
          show
          variant="danger"
          class="mb-0"
        >
          <div class="alert-body">
            {{ error }}
          </div>
        </b-alert>

        <div
          v-else-if="!projets.length"
          class="text-center text-muted py-3"
        >
          Aucun projet d'amélioration continue pour le moment.
        </div>

        <b-table
          v-else
          :items="projets"
          :fields="fields"
          responsive
          hover
          class="mb-0 cursor-pointer-rows"
          @row-clicked="voirChantiers"
        >
          <template #cell(nom)="data">
            <span class="font-weight-bold text-primary">{{ data.item.nom }}</span>
          </template>
          <template #cell(description)="data">
            <span class="text-muted">{{ truncate(data.item.description) }}</span>
          </template>
          <template #cell(nbChantiers)="data">
            <b-badge variant="light-primary">
              {{ data.item.nbChantiers }}
            </b-badge>
          </template>
          <template #cell(created)="data">
            {{ formatDate(data.item.created) }}
          </template>
          <template #cell(actions)="data">
            <div
              class="d-inline-flex"
              @click.stop
            >
              <b-button
                size="sm"
                variant="flat-primary"
                title="Voir les chantiers du projet"
                @click="voirChantiers(data.item)"
              >
                <feather-icon icon="ListIcon" />
              </b-button>
              <b-button
                size="sm"
                variant="flat-secondary"
                title="Télécharger le PDF récapitulatif"
                :disabled="downloadingId === data.item.id"
                @click="downloadPdf(data.item)"
              >
                <b-spinner
                  v-if="downloadingId === data.item.id"
                  small
                />
                <feather-icon
                  v-else
                  icon="FileTextIcon"
                />
              </b-button>
              <b-button
                size="sm"
                variant="flat-secondary"
                title="Gérer le projet"
                :to="{ name: 'chantiers-projet-detail', params: { id: data.item.id } }"
              >
                <feather-icon icon="SettingsIcon" />
              </b-button>
              <b-button
                v-if="$can('CHANTIER:update')"
                size="sm"
                variant="flat-secondary"
                title="Modifier"
                @click="openEdit(data.item)"
              >
                <feather-icon icon="Edit2Icon" />
              </b-button>
              <b-button
                v-if="$can('CHANTIER:delete')"
                size="sm"
                variant="flat-danger"
                title="Supprimer"
                @click="confirmDelete(data.item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>

        <div
          v-if="!loading && totalCount > pageSize"
          class="d-flex justify-content-center mt-1"
        >
          <b-pagination
            v-model="pageNumber"
            :total-rows="totalCount"
            :per-page="pageSize"
          />
        </div>
      </b-card>

      <chantier-projet-form-modal
        ref="formModal"
        @saved="onSaved"
      />
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BInputGroup, BInputGroupPrepend, BFormInput,
  BTable, BBadge, BPagination, BSpinner, BAlert,
} from 'bootstrap-vue'
import ChantierProjetFormModal from '@/components/chantierProjets/ChantierProjetFormModal.vue'

export default {
  name: 'ChantierProjetsListView',
  components: {
    BCard,
    BButton,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BSpinner,
    BAlert,
    ChantierProjetFormModal,
  },
  data() {
    return {
      projets: [],
      totalCount: 0,
      pageNumber: 1,
      pageSize: 12,
      search: '',
      loading: true,
      error: null,
      downloadingId: null,
      fields: [
        { key: 'nom', label: 'Nom' },
        { key: 'description', label: 'Description' },
        { key: 'nbChantiers', label: 'Chantiers' },
        { key: 'created', label: 'Créé le' },
        { key: 'actions', label: 'Actions', class: 'text-nowrap' },
      ],
    }
  },
  watch: {
    search() {
      if (this.pageNumber !== 1) this.pageNumber = 1
      else this.load()
    },
    pageNumber() { this.load() },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const response = await this.$http.myressif.chantierProjets.search({
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          search: this.search ? this.search.trim() : undefined,
        })
        this.projets = (response && response.items) || []
        this.totalCount = (response && response.totalCount) || 0
      } catch (e) {
        this.projets = []
        this.totalCount = 0
        this.error = 'Une erreur est survenue lors du chargement des projets.'
      } finally {
        this.loading = false
      }
    },
    // Clic sur un projet → liste des chantiers pré-filtrée sur ce projet.
    voirChantiers(projet) {
      this.$router.push({ name: 'chantiers-list', query: { projetId: projet.id } })
    },
    async downloadPdf(projet) {
      if (this.downloadingId) return
      this.downloadingId = projet.id
      try {
        const response = await this.$http.myressif.chantierProjets.downloadRecapPdf(projet.id)
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const slug = (projet.nom || 'projet')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '')
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `recapitulatif-projet-${slug || 'projet'}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$bvModal.msgBoxOk('Impossible de générer le PDF récapitulatif.', {
          title: 'Erreur', okVariant: 'danger', centered: true,
        })
      } finally {
        this.downloadingId = null
      }
    },
    openCreate() {
      this.$refs.formModal.open(null)
    },
    openEdit(projet) {
      this.$refs.formModal.open(projet)
    },
    onSaved() {
      this.load()
    },
    confirmDelete(projet) {
      this.$bvModal
        .msgBoxConfirm(`Supprimer le projet « ${projet.nom} » ? Les chantiers rattachés seront conservés (détachés).`, {
          title: 'Confirmer la suppression',
          okTitle: 'Supprimer',
          okVariant: 'danger',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(async value => {
          if (value !== true) return
          try {
            await this.$http.myressif.chantierProjets.delete(projet.id)
            await this.load()
          } catch (e) {
            this.error = 'La suppression du projet a échoué.'
          }
        })
    },
    truncate(text) {
      if (!text) return '—'
      return text.length > 90 ? `${text.slice(0, 90)}…` : text
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped>
/* Ligne de projet cliquable → liste des chantiers du projet */
::v-deep .cursor-pointer-rows tbody tr {
  cursor: pointer;
}
</style>
